"use client";
import { LoaderComponent } from "@/components";
import { useRouter } from "@/i18n/routing";
import {
  CurrencyConverter,
  GET_PRODUCT_APPLICATION,
  IMAGES,
  PRODUCT_TYPES,
  PRODUCTS,
  STAGE,
  STAGE_BASED_ROUTING,
  SUB_STAGE_FAILS,
  WEB_COLORS,
  SUBSTAGE,
  DECISION,
  MOENGAGE_CODES,
} from "@/lib/constants";
import useErrorHandling from "@/lib/hooks/errorhandling";
import useStageErrorHandler from "@/lib/hooks/stageErrorHandler";
import {
  Box,
  Button,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslations } from "next-intl";
import Image from "next/image";
import styles from "./pre-approved-offers.module.css";
import {
  useGetApplicationQuery,
  useGetESignQuery,
  usePostOptForLoanOfferMutation,
} from "@/lib/redux";
import { useDispatch } from "react-redux";
import { setModalAttributes } from "@/lib/redux/slice/ModalSlice";
import { setProductType } from "@/lib/redux/slice/ApplicationSlice";
import { useMemo, useState } from "react";
import { DateUtils } from "@/lib/utils/common.utils";
import { setMoEngageUserAttributes } from "@/lib/Moengage";

export default function LoanOfferCard({ offer }) {
  const { amount, type, isTaken, expiryDate } = offer;
  const [loader, setLoader] = useState(false);

  const {
    data: applicationData,
    isLoading: fetchingApplications,
    error: applicationFetchError,
    refetch: refetchApplication,
  } = useGetApplicationQuery("", {
    refetchOnMountOrArgChange: true,
  });

  const repeatLoanApplication = GET_PRODUCT_APPLICATION(
    PRODUCTS.RL,
    applicationData?.data,
  );

  const t = useTranslations("Dashboard");
  const theme = useTheme();
  const isBelowMd = useMediaQuery(theme.breakpoints.down("md"));
  const isBelowSm = useMediaQuery(theme.breakpoints.down("sm"));
  const router = useRouter();

  const dispatch = useDispatch();

  const [mutateOptForLoanOffer, { isLoading, data, error }] =
    usePostOptForLoanOfferMutation();

  const {
    data: eSignData,
    isLoading: isLoadingESign,
    error: errorESign,
  } = useGetESignQuery(
    { appId: repeatLoanApplication?.appId },
    {
      skip:
        !repeatLoanApplication?.appId ||
        repeatLoanApplication?.stage !== STAGE.DISBURSED,
    },
  );

  const hideExpiry =
    !Boolean(expiryDate) || repeatLoanApplication?.stage === STAGE.DISBURSED;

  const dataToDisplay = useMemo(() => {
    let data = {};
    const rlDisbursed = repeatLoanApplication?.stage === STAGE.DISBURSED;
    const disbursedAmount = eSignData?.data?.loanAmount;

    if (rlDisbursed) {
      data = {
        amount: CurrencyConverter?.(disbursedAmount) ?? disbursedAmount,
        text: t("loanDisbursedSuccessfully"),
      };
    } else {
      data = {
        amount: CurrencyConverter?.(amount) ?? amount,
        text: t("additionalLoanEligibility"),
      };
    }

    data.buttonLabel = isTaken
      ? rlDisbursed
        ? t("viewLoanDetails")
        : t("resumeJourney")
      : t("getNow");

    return data;
  }, [
    amount,
    repeatLoanApplication?.stage,
    isTaken,
    eSignData?.data?.disburmentAmount,
  ]);

  const handleGetRpl = async () => {
    setLoader(true);
    if (isTaken) {
      const refetchApplicationData = await refetchApplication().unwrap();
      const productApplication = GET_PRODUCT_APPLICATION(
        PRODUCTS.RL,
        refetchApplicationData?.data,
      );
      if (!productApplication) {
        dispatch(
          setModalAttributes({
            open: true,
            message: "Sorry! You are currently not eligible for the offer",
            // type: STAGE?.CLOSED,
          }),
        );
        setLoader(false);
        return;
      }
      dispatch(setProductType(PRODUCT_TYPES.REPEAT_LOAN));
      const isApplicationClosedOrFailed =
        productApplication?.stage !== undefined &&
        (productApplication?.stage === STAGE?.CLOSED ||
          productApplication?.stage === STAGE?.CREDIT_QUEUE ||
          SUB_STAGE_FAILS.includes(productApplication?.subStage));

      if (isApplicationClosedOrFailed) {
        dispatch(
          setModalAttributes({
            open: true,
            message: "Sorry! You are currently not eligible for the offer",
            type:
              productApplication?.stage === STAGE?.CLOSED
                ? STAGE?.CLOSED
                : STAGE?.CREDIT_QUEUE,
          }),
        );
        setLoader(false);
        return;
      }

      if (
        productApplication?.stage === STAGE?.MANDATE &&
        productApplication?.ucicDecision?.toLowerCase() !== DECISION.ACCEPT
      ) {
        router.push("/repeat-loan/mandate/repayment-banking");
      } else {
        router.push(
          STAGE_BASED_ROUTING(
            productApplication?.stage,
            PRODUCT_TYPES.REPEAT_LOAN,
          ),
        );
      }

      setLoader(false);
    } else {
      try {
        const response = await mutateOptForLoanOffer({ type }).unwrap();
        // Refetch the application to get the latest data in case previous application was cancelled
        await refetchApplication();
        setMoEngageUserAttributes({
          [MOENGAGE_CODES.USER_ATTRIBUTES.RL_APP_ID]: response?.data?.appId,
          [MOENGAGE_CODES.USER_ATTRIBUTES.RL_OFFER_EXPIRY]: expiryDate,
          [MOENGAGE_CODES.USER_ATTRIBUTES.RL_Offer_Amount]: amount,
        });
        if (response?.statusCode < 400 && response?.data?.stage) {
          dispatch(setProductType(PRODUCT_TYPES.REPEAT_LOAN));
          router.replace(
            STAGE_BASED_ROUTING(response.data.stage, PRODUCT_TYPES.REPEAT_LOAN),
          );
        }
        return;
      } catch (err) {
        console.log("Error opting for loan offer:", err);
        const statusCode = err?.status;
        const errData = err?.data;
        const errorDetail = errData?.detail;
        const errorDetailMessage =
          typeof errorDetail === "string" ? errorDetail : errorDetail?.message;
        const msg =
          errData?.message ||
          errorDetailMessage ||
          "Sorry! Something went wrong. Please try again later.";
        dispatch(
          setModalAttributes({
            open: true,
            message: msg,
            // type: STAGE.CLOSED,
          }),
        );
      } finally {
        setLoader(false);
      }
    }
  };
  return (
    <Stack
      className={styles["pre-approved-offer"]}
      sx={{
        padding: { xs: "20px", md: "30px" },
        mb: { md: 2 },
      }}
    >
      <LoaderComponent
        open={loader || fetchingApplications || isLoadingESign}
      />
      <Grid container justifyContent={"space-between"} alignItems={"center"}>
        <Grid item xs={8} md={8}>
          <Stack alignItems={"flex-start"} rowGap={1}>
            <Box
              flexDirection={"row"}
              display={hideExpiry ? "none" : "flex"}
              alignItems={"center"}
              textTransform={"uppercase"}
            >
              <Typography
                display={"inline"}
                fontWeight={{ xs: 400 }}
                color={WEB_COLORS.fadeColor2}
                fontSize={{ xs: "12px", md: "16px" }}
                marginRight={"3px"}
              >
                {t("offerValidTill")}
              </Typography>
              <Typography
                display={"inline"}
                fontWeight={{ xs: 400 }}
                color={WEB_COLORS.fadeColor2}
                fontSize={{ xs: "12px", md: "16px" }}
                marginRight={"3px"}
              >
                {expiryDate ? DateUtils.toShortMonthDate(expiryDate) : "N/A"}
              </Typography>
            </Box>
            <Box flexDirection={"row"} alignItems={"center"}>
              <Typography
                display={"inline"}
                fontWeight={{ xs: 400 }}
                color={WEB_COLORS.fadeColor2}
                fontSize={{ xs: "16px", md: "20px", lg: "24px" }}
                marginRight={"3px"}
              >
                {dataToDisplay?.text}
              </Typography>
              <br />
              <Typography
                display={"inline"}
                fontWeight={{ xs: 700, md: 500 }}
                color={WEB_COLORS.primary}
                fontSize={{ xs: "24px", md: "38px" }}
                marginRight={"3px"}
              >
                {dataToDisplay?.amount}
              </Typography>
            </Box>

            <Button
              stackwidth={"176px"}
              sx={{
                color: WEB_COLORS.pale,
                backgroundColor: WEB_COLORS.primary,
                textTransform: "none",
                fontWeight: 400,
                m: 0,
                width: { xs: "140px", sm: "176px" },
                "&:hover": { backgroundColor: WEB_COLORS.primaryLightShade },
                fontSize: { xs: "14px", md: "16px" },
              }}
              disabled={loader}
              onClick={() => handleGetRpl()}
            >
              {dataToDisplay?.buttonLabel}
            </Button>
          </Stack>
        </Grid>
        <Grid item xs={3} sm={2} md={3} justifyContent={"flex-end"}>
          <Image
            src={IMAGES.moneyGroup}
            alt="Money Sack"
            width={isBelowMd ? (isBelowSm ? 70 : 100) : 150}
          />
        </Grid>
      </Grid>
    </Stack>
  );
}
